import React, { useState, useEffect, ChangeEvent, FormEvent, useCallback, useRef, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Input } from "reactstrap";
import debounce from 'lodash/debounce';
import { unstable_batchedUpdates } from "react-dom";
//redux
import { useSelector, useDispatch } from "react-redux";
import withRouter from "src/components/Common/withRouter";

//Import Breadcrumb
import Breadcrumb from "../../../components/Common/Breadcrumb";

import { editProfile } from "../../../slices/thunks";
import { loginById } from "../../../slices/thunks";
import { createSelector } from "reselect";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { RootState } from "../../../slices/store";
import axiosInstance from "src/common/axiosInstance";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { normalizePhoneNumber } from "src/helpers/phoneHelper";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { usePageAccess, PAGE_ACCESS_TYPES } from '../../../components/Common/useAuthRedirect';
import "./clientUsers.css"
interface FormData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  profilePic: string;
  mobile: string;
  users_id: number;
  profileFile?: File | null;
  old_password: string,
  new_password: string,
  title: string,
  about: string,
  location: string,
}

interface City {
  id: number;
  name: string;
}


const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Memoized selector for better performance
  const selectUserData = useMemo(() => createSelector(
    [(state: RootState) => state.profile, (state: any) => state.login],
    (profile, login) => profile.success ? profile.success : login.user.user
  ), []);

  const profile = useSelector((state: RootState) => state.profile);
  const { user } = useSelector((state: any) => state.login);
  const agencyData = useSelector((state: any) => state.agency.agency.data);
  const obj = useSelector(selectUserData);
  
  const [isVisible, setIsVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [errors, setErrors] = useState({ firstName: false, lastName: false });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const emailCheckRef = useRef<AbortController | null>(null);
  const mobileCheckRef = useRef<AbortController | null>(null);
  const [passErrors, setPassErrors] = useState({
    old_password: "",
    new_password: "",
  });

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    profilePic: "",
    mobile: "",
    users_id: 0,
    profileFile: null as File | null,
    old_password: "",
    new_password: "",
    title: "",
    about: "",
    location: "",
  });
  const [originalData, setOriginalData] = useState<FormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    profilePic: "",
    mobile: "",
    users_id: 0,
    profileFile: null as File | null,
    old_password: "",
    new_password: "",
    title: "",
    about: "",
    location: "",
  });

  usePageAccess(PAGE_ACCESS_TYPES.CLIENT_USERS_ONLY);

  // Memoize username calculation instead of useEffect
  const computedUsername = useMemo(() => 
    `${formData.firstName} ${formData.lastName}`.trim(), 
    [formData.firstName, formData.lastName]
  );

  // Memoize user initials
  const userInitials = useMemo(() => {
    if (!computedUsername) return '';
    const words = computedUsername.trim().split(' ').filter(Boolean);
    const firstInitial = words[0]?.charAt(0).toUpperCase() || '';
    const secondInitial = words.length > 1 ? words[1]?.charAt(0).toUpperCase() : '';
    return firstInitial + secondInitial;
  }, [computedUsername]);

  // Debounced validation
  const debouncedValidation = useMemo(
    () => debounce((name: string, value: string) => {
      if (!isVisible) return;

      const newErrors = { ...passErrors };

      if (name === "new_password") {
        if (value === formData.old_password) {
          newErrors.new_password = "New password should not be the same as old password.";
        } else if (!/^.{14,}$/.test(value)) {
          newErrors.new_password = "Your password must be at least 14 characters long.";
        } else if (!/(?=.*[a-z])/.test(value)) {
          newErrors.new_password = "Your password must contain at least one lowercase letter.";
        } else if (!/(?=.*[A-Z])/.test(value)) {
          newErrors.new_password = "Your password must contain at least one uppercase letter.";
        } else if (!/(?=.*\d)/.test(value)) {
          newErrors.new_password = "Your password must include at least one number.";
        } else if (!/(?=.*[@$!%*?&])/.test(value)) {
          newErrors.new_password = "Your password must include at least one special character (e.g.,!,@,#,$,%)";
        } else {
          newErrors.new_password = "";
        }
      }

      if (name === "old_password") {
        newErrors.old_password = value.trim() === "" ? "Old password is required." : "";
      }

      setPassErrors(newErrors);
    }, 300),
    [isVisible, formData.old_password, passErrors]
  );

  useEffect(() => {
    // Pre-fill form data in Edit mode
    const userData = obj;
    const initialData = {
      firstName: userData.FirstName || "",
      lastName: userData.LastName || "",
      username: userData.name || "",
      email: userData.email || "",
      profilePic: userData.profile_picture_url || "",
      mobile: normalizePhoneNumber(userData.mobile) || "",
      users_id: userData.users_id || 0,
      title: userData.title || "",
      about: userData.about || "",
      location: userData.location || "",
      profileFile: null as File | null,
      old_password: "",
      new_password: "",
    };
    
    setFormData((prevState) => ({
      ...prevState,
      ...initialData
    }));
    
    // Store original data for comparison
    setOriginalData(initialData);
  }, [obj]);

  const hasDataChanged = useCallback(() => {
    // Check basic fields
    const basicFieldsChanged = 
      formData.firstName !== originalData.firstName ||
      formData.lastName !== originalData.lastName ||
      formData.mobile !== originalData.mobile ||
      formData.title !== originalData.title ||
      formData.about !== originalData.about ||
      formData.location !== originalData.location;
    
    // Check if profile picture was changed (new file uploaded)
    const profilePicChanged = formData.profileFile !== null;
    
    // Check if password fields are filled (password change)
    const passwordChanged = formData.old_password.trim() !== "" || formData.new_password.trim() !== "";
    
    return basicFieldsChanged || profilePicChanged || passwordChanged;
  }, [formData, originalData]);

  const handlePhoneChange = useCallback((phone: string) => {
    setFormData((prev) => ({
      ...prev,
      mobile: phone,
    }));
    setPhoneError(null);
  }, []);

  const validateForm = async (): Promise<boolean> => {
    if (formData.mobile !== "") {
      const phoneNumber = parsePhoneNumberFromString('+' + formData.mobile);
      // Check valid format for selected country
      if (!phoneNumber?.isValid()) {
        setPhoneError('Please enter a valid phone number for the selected country.');
        return false;
      }
      setPhoneError(null);
      
      try {
        const mobileResponse = await axiosInstance.post('/check-mobile', {
          mobile: formData.mobile,
          users_id: formData.users_id,
        });
        if (mobileResponse.data.data.exists) {
          toast.error('This mobile number is already associated with an active user.');
          return false;
        }
      } catch (error) {
        console.error('Error checking mobile:', error);
        toast.error('Unable to validate mobile number. Please try again later.');
        return false;
      }
    }
    return true;
  };


  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, type, value } = e.target;

    let newValue = value;

    if (name === "firstName" || name === "lastName") {
      let sanitizedValue = value.replace(/[^a-zA-Z\s]/g, "");

      if (sanitizedValue.length > 50) {
        sanitizedValue = sanitizedValue.slice(0, 50);
      }

      // Batch state updates for better performance
      unstable_batchedUpdates(() => {
        setFormData((prev) => ({
          ...prev,
          [name]: sanitizedValue,
        }));
        setErrors(prev => ({ ...prev, [name]: sanitizedValue.trim() === "" }));
      });
      return;
    }

    if (type === "checkbox" && e.target instanceof HTMLInputElement) {
      const { checked } = e.target; // Safe to access 'checked' here
      const [section, field] = name.split(".");
      if (section && field) {
        setFormData((prev) => ({
          ...prev,
          [section]: {
            ...(prev as any)[section],
            [field]: checked,
          },
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      // Batch state updates
      unstable_batchedUpdates(() => {
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: value.trim() === "" }));
      });
    }
    
    // Use debounced validation instead of immediate validation
    debouncedValidation(name, value);
  }, [debouncedValidation]);

  const validateInput = (name: string, value: string) => {
    if (!isVisible) return;

    const newErrors = { ...passErrors };

    if (name === "new_password") {
      if (value === formData.old_password) {
        newErrors.new_password = "New password should not be the same as old password.";
      } else if (!/^.{14,}$/.test(value)) {
        newErrors.new_password = "Your password must be at least 14 characters long.";
      } else if (!/(?=.*[a-z])/.test(value)) {
        newErrors.new_password = "Your password must contain at least one lowercase letter.";
      } else if (!/(?=.*[A-Z])/.test(value)) {
        newErrors.new_password = "Your password must contain at least one uppercase letter.";
      } else if (!/(?=.*\d)/.test(value)) {
        newErrors.new_password = "Your password must include at least one number.";
      } else if (!/(?=.*[@$!%*?&])/.test(value)) {
        newErrors.new_password = "Your password must include at least one special character (e.g.,!,@,#,$,%)";
      } else {
        newErrors.new_password = "";
      }
    }

    if (name === "old_password") {
      newErrors.old_password = value.trim() === "" ? "Old password is required." : "";
    }

    setPassErrors(newErrors);
  };

  const validatePasswords = () => {
    const newErrors = {
      old_password: "",
      new_password: ""
    };
    let isValid = true;

    if (isVisible) {
      if (formData.old_password.trim() === "") {
        newErrors.old_password = "Old password is required.";
        isValid = false;
      }

      if (formData.new_password.trim() === "") {
        newErrors.new_password = "New password is required.";
        isValid = false;
      } else if (formData.new_password === formData.old_password) {
        newErrors.new_password = "New password should not be the same as old password.";
        isValid = false;
      } else if (!/^.{14,}$/.test(formData.new_password)) {
        newErrors.new_password = "Your password must be at least 14 characters long.";
        isValid = false;
      } else if (!/(?=.*[a-z])/.test(formData.new_password)) {
        newErrors.new_password = "Your password must contain at least one lowercase letter.";
        isValid = false;
      } else if (!/(?=.*[A-Z])/.test(formData.new_password)) {
        newErrors.new_password = "Your password must contain at least one uppercase letter.";
        isValid = false;
      } else if (!/(?=.*\d)/.test(formData.new_password)) {
        newErrors.new_password = "Your password must include at least one number.";
        isValid = false;
      } else if (!/(?=.*[@$!%*?&])/.test(formData.new_password)) {
        newErrors.new_password = "Your password must include at least one special character (e.g.,!,@,#,$,%)";
        isValid = false;
      }
    }

    setPassErrors(newErrors);
    return isValid;
  };

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement && e.target.type === "file") {
      const { files } = e.target;

      if (files && files[0]) {
        const file = files[0];
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(file.type)) {
          toast.error("Invalid file type. Only JPG,JPEG and PNG are allowed.");
          e.target.value = '';
          return;
        }

        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
          // Use requestIdleCallback for non-blocking validation
          const processImage = () => {
            const maxWidth = 500;
            const maxHeight = 500;
            if (img.width > maxWidth || img.height > maxHeight) {
              toast.error(`Profile picture should not exceed ${maxWidth}px * ${maxHeight}px.`);
              e.target.value = '';
              return;
            }
            const convertedFile = new File([file], file.name, { type: file.type });
            setFormData((prevData) => ({
              ...prevData,
              profilePic: URL.createObjectURL(file),
              profileFile: convertedFile,
            }));
          };

          if (typeof window.requestIdleCallback === "function") {
            window.requestIdleCallback(processImage);
          } else {
            setTimeout(processImage, 0);
          }
          
        };
      }
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!hasDataChanged()) {
      toast.info('No data to update');
      setLoading(false);
      return;
    }

    // Validate required fields
    setErrors({
      firstName: formData.firstName.trim() === "",
      lastName: formData.lastName.trim() === "",
    });

    if (formData.firstName.trim() === "" || formData.lastName.trim() === "") {
      const error = formData.firstName == '' ? "First Name is required" : "Last Name is required";
      toast.error(error);
      setLoading(false);
      return;
    }

    // Skip email validation since email field is disabled and unchangeable


    // Validate passwords if visible
    if (isVisible && !validatePasswords()) {
      setLoading(false);
      return;
    }

    const isValid = await validateForm();
    if (!isValid) {
      setLoading(false);
      return;
    }

    // Check mobile uniqueness only (email is disabled and unchangeable)
    try {
      if (formData.mobile) {
        const mobileExists = await axiosInstance.post('/check-mobile', { 
          mobile: formData.mobile, 
          users_id: formData.users_id 
        }).then(res => res.data.data.exists);

        if (mobileExists) {
          toast.error('This mobile number is already associated with an active user.');
          setLoading(false);
          return;
        }
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'Timeout') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error('An error occurred while validating mobile number. Please try again.');
      }
      console.error('Mobile validation error:', error);
      setLoading(false);
      return;
    }

    if (formData.title.length > 100) {
      toast.error("Job Title should not exceed 100 characters!");
      setLoading(false);
      return;
    }

    // Proceed with form submission
    const formDataToSend = new FormData();
    formDataToSend.append("fname", formData.firstName);
    formDataToSend.append("lname", formData.lastName);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("mobile", formData.mobile);
    formDataToSend.append("users_id", String(formData.users_id));
    formDataToSend.append("new_pass", formData.new_password);
    formDataToSend.append("old_pass", formData.old_password);
    formDataToSend.append("about", formData.about);
    formDataToSend.append("location", formData.location);
    formDataToSend.append("title", formData.title);

    if (formData.profileFile) {
      formDataToSend.append("profileFile", formData.profileFile);
    }

    try {
      const response = await dispatch(editProfile(formDataToSend));

      if (response.data.exists) {
        toast.error('Invalid old password.');
      } else {
        // Await loginById to ensure fresh data is loaded before showing success
        await dispatch(loginById(obj.users_id));
        toast.success('Your profile is updated!');
        setSearchValue('');
        setCities([]);
      }
      if (isVisible) {
        toggleVisibility();
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong. Please try again later.', {
        style: {
          paddingRight: "2rem", // space for the close (x) button
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = useCallback(() => {
    const newErrors = {
      old_password: "",
      new_password: ""
    };
    setPassErrors(newErrors);
    setFormData((prev) => ({ ...prev, old_password: "", new_password: "", }));
    setIsVisible((prev) => !prev);
  }, []);

  const searchCity = useCallback((e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;
    setSearchValue(value);
    fetchCities(value);
  }, []);

  const fetchCities = useCallback(
    debounce(async (query: string) => {
      if (query.length < 3) {
        setCities([]);
        return;
      }
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Create new abort controller
      abortControllerRef.current = new AbortController();
      setCityLoading(true);
      
      try {
        const response = await axiosInstance.get(`/cities`, {
          params: { city: query },
          signal: abortControllerRef.current.signal
        });
        setCities(response.data.data.cities);
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error('Error fetching cities:', error);
        }
      } finally {
        setCityLoading(false);
      }
    }, 300),
    []
  );

  // Add request cancellation for email checking
  const checkEmailDuplicate = useCallback(async (email: string, users_id: number) => {
    if (emailCheckRef.current) {
      emailCheckRef.current.abort();
    }

    emailCheckRef.current = new AbortController();

    try {
      const response = await axiosInstance.post('/check-email', 
        { email, users_id },
        { signal: emailCheckRef.current.signal }
      );
      return response.data.data.exists;
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        throw error;
      }
      return false;
    }
  }, []);

  // Add request cancellation for mobile checking
  const checkMobileDuplicate = useCallback(async (mobile: string, users_id: number) => {
    if (mobileCheckRef.current) {
      mobileCheckRef.current.abort();
    }

    mobileCheckRef.current = new AbortController();

    try {
      const response = await axiosInstance.post('/check-mobile', 
        { mobile, users_id },
        { signal: mobileCheckRef.current.signal }
      );
      return response.data.data.exists;
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        throw error;
      }
      return false;
    }
  }, []);

  const handleCitySelect = useCallback((city: any) => {
    setFormData((prev) => ({ ...prev, location: city.name, }));
    setCities([]);
    setSearchValue('');
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cancel all pending requests on unmount
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (emailCheckRef.current) emailCheckRef.current.abort();
      if (mobileCheckRef.current) mobileCheckRef.current.abort();
      // Cancel debounced functions
      debouncedValidation.cancel();
      fetchCities.cancel();
    };
  }, [debouncedValidation, fetchCities]);

  const formatPhoneNumber = useCallback((value: string) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);
    if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return cleaned;
    }
  }, []);

  return (
    <React.Fragment>
      <div className="page-content profile-page" style={{ marginTop: "0" }}>
        <Container fluid style={{ maxWidth: "100%"}}>
          <Breadcrumb title="User" breadcrumbItem="Profile" titleLink="/analytics" />
                <Card>
                  <CardBody>
                    <Row className="justify-content-center">
                      <div className="bg-white" >
                        <div className="d-flex align-items-center">
                          <div className="position-relative me-3">
                            <div className="avatar-lg">
                              {formData.profilePic ? (
                                <img
                                  src={formData.profilePic}
                                  alt="Profile"
                                  className="rounded-circle img-thumbnail"
                                  style={{ width: "60px", height: "60px", objectFit: "cover" }}
                                />
                              ) : (
                                <div
                                  className="rounded-circle d-flex align-items-center justify-content-center text-white img-thumbnail"
                                  style={{
                                    width: "60px",
                                    height: "60px",
                                    fontWeight: "bold",
                                    backgroundColor: "#607d8b",
                                    fontSize: "24px"
                                  }}
                                >
                                   {userInitials}
                                </div>
                              )}
                            </div>
                                <label
                                    htmlFor="profilePicInput"
                                    title="Profile Picture"
                                    className="d-block position-absolute end-0"
                                    style={{ 
                                    cursor: "pointer",
                                    bottom: "5px",
                                    left: "40px" 
                                    }}
                                >
                                    <div className="avatar-sm">
                                        <div className="avatar-title rounded-circle bg-primary text-white">
                                            <i className="mdi mdi-pencil"></i>
                                        </div>
                                    </div>
                                  
                              </label>
                              <input
                                type="file"
                                id="profilePicInput"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                              />
                            </div>
                            <div style={{ width: '100%' }}>
                              <h5 className="mb-1">{computedUsername}</h5>
                              <p 
                                className="text-muted mb-0"
                                style={{
                                  wordBreak: 'break-word',       
                                  overflowWrap: 'break-word',    
                                  whiteSpace: 'normal'
                                }}
                              >
                                {formData.title}
                              </p>
                            </div>
                          </div>
                        </div>
                     
                      </Row>
                        <hr style={{margin :"15px 0 24px 0"}} />
                        <div className="row mb-3 gx-4 gx-md-5">
                          <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                            <label>First Name</label>
                            <input
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                              className="form-control shadow-sm rounded"
                              id="formrow-firstname-input"
                              placeholder="First Name"
                            />
                            {errors.firstName && <span className="text-danger" style={{display:"inline-block",marginTop:"10px"}}>First Name is required</span>}
                          </div>
                          <div className="col-12 col-sm-6">
                            <label>Last Name</label>
                            <input
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                              className="form-control shadow-sm rounded"
                              id="formrow-lastname-input"
                              placeholder="Last Name"
                            />
                            {errors.lastName && <span className="text-danger" style={{display:"inline-block",marginTop:"10px"}}>Last Name is required</span>}
                          </div>
                        </div>
                        <div className="row mb-3 gx-4 gx-md-5">
                          <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                            <label>Email</label>
                            <input
                              type="text"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="form-control shadow-sm rounded"
                              id="formrow-firstname-input"
                              placeholder="Email"
                              disabled={true}
                              maxLength={50}
                            />
                          </div>
                          <div className="col-12 col-sm-6">
                            <label>Mobile</label>
                            <PhoneInput
                              country="us"
                              value={normalizePhoneNumber(formData.mobile)}
                              onChange={handlePhoneChange}
                              inputStyle={{ width: '100%',height:"40px" }}
                            />
                            {phoneError && (
                              <div style={{ color: 'red', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                {phoneError}
                              </div>)}
                          </div>
                        </div>
                        <div className="row mb-3 gx-4 gx-md-5">
                          <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                            <label>Job Title</label>
                            <input
                              type="text"
                              name="title"
                              value={formData.title}
                              onChange={handleInputChange}
                              className="form-control shadow-sm rounded"
                              id="formrow-title-input"
                              placeholder="Title"
                            />
                          </div>
                          <div className="col-12 col-sm-6">
                            <label>Website</label>
                            <input
                              type="text"
                              value={agencyData.agency_domain ? `https://${agencyData.agency_domain}.unifieddatastudio.com` : "https://q1media.unifieddatastudio.com"}
                              name="website"
                              className="form-control"
                              id="gen-info-website-input"
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="row mb-3 gx-4 gx-md-5">
                          <div className="col-12 col-sm-6 mb-3 mb-sm-0">
                            <label>Location</label>
                            <input
                              type="text"
                              name="location"
                              value={formData.location}
                              onChange={handleInputChange}
                              className="form-control shadow-sm rounded"
                              id="formrow-location-input"
                              placeholder="Location"
                              readOnly
                            />
                          </div>
                          <div className="col-12 col-sm-6">
                            <label>Search Location</label>
                            <input
                              type="text"
                              name="search_loc"
                              value={searchValue}
                              onChange={searchCity}
                              className="form-control shadow-sm rounded"
                              id="formrow-search_loc-input"
                              placeholder="Type to search..."
                            />
                            {/* Display matching accounts as a dropdown */}
                            {cityLoading && (
                              <div className="position-absolute w-100 bg-white shadow-sm rounded p-2" style={{ zIndex: 1000 }}>
                                <span className="text-muted">Loading...</span>
                              </div>
                            )}
                            {cities.length > 0 && !cityLoading && (
                              <ul
                                className="cities-dropdown list-group position-absolute bg-white shadow-sm rounded"
                              >
                                {cities.map((city) => (
                                  <li
                                    key={city.id}
                                    className="list-group-item"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleCitySelect(city)}
                                  >
                                    {city.name}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col">
                            <label>About</label>
                            <Input
                              type="textarea"
                              style={{ height: "100px" }}
                              name="about"
                              className="form-control shadow-sm rounded"
                              placeholder="Type something about yourself..."
                              id="formrow-about-input"
                              value={formData.about}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                    </CardBody>
                  </Card>
                  <Card style={{margin:"20px 0 0 0"}}>
                    <CardBody style={{ padding: "10px" }}>
                          <div className="d-flex align-items-start" onClick={toggleVisibility}>
                            <div className="w-100 d-flex justify-content-between align-items-center">
                                <h5 className="card-title">Change Password ?</h5>
                                <span>
                                    <img
                                        src="/assets/icons/dropdown.svg"
                                        alt="Dropdown Icon"
                                        className={`nav-icon ${isVisible ? "rotate" : ""}`}
                                    />
                                </span>
                            </div>
                          </div>
                          {isVisible && (
                            <div className="row mb-3 gx-4 gx-md-5">
                              <div className="col-12 col-sm-6 mb-3 mb-sm-0 mt-2">
                                <label>Old Password</label>
                                <input
                                  type="password"
                                  name="old_password"
                                  value={formData.old_password}
                                  onChange={handleInputChange}
                                  className="form-control shadow-sm rounded"
                                  id="formrow-old-password-input"
                                  placeholder="Old Password"
                                />
                                {passErrors.old_password && <span className="text-danger" style={{display:"inline-block",marginTop:"10px"}} >{passErrors.old_password}</span>}
                              </div>
                              <div className="col-12 col-sm-6 mt-2">
                                <label>New Password</label>
                                <input
                                  type="password"
                                  name="new_password"
                                  value={formData.new_password}
                                  onChange={handleInputChange}
                                  className="form-control shadow-sm rounded"
                                  id="formrow-new-password-input"
                                  placeholder="New Password"
                                />
                                {passErrors.new_password && <span className="text-danger" style={{display:"inline-block",marginTop:"10px"}}>{passErrors.new_password}</span>}
                              </div>
                            </div>
                          )}

                    </CardBody>
                  </Card>

                  <div className="d-flex justify-content-end" style={{margin:"30px 0 20px 0"}}>
                    <button type="submit" onClick={handleSubmit} className="btn btn-primary button-primary flex-grow-1 flex-sm-grow-0" disabled={loading}>
                      {loading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        "Update Profile"
                      )}
                    </button>
                  </div>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
